<template>
	<div class="mx-auto p-3 sm:p-4 h-screen">

		<div class="mx-auto md:h-full flex flex-col">
			<!-- Butoni për t'u kthyer -->
			<div class="btn-grup flex justify-between pb-3 border-b border-gray-300 mb-3">
				<button
					class="flex flex-row hover:bg-gray-200 items-center rounded py-2 px-4 text-center font-sans mr-2 text-xs font-bold hover:text-primary-500 transition-all bg-transparent border border-gray-300 text-primary"
					type="button"
					@click="handleBackButtonClick"
				>
					<i class="tabler:arrow-left w-5 h-5 mr-1"></i>
					<span class="hidden md:inline">{{ labels.backbtn }}</span>
				</button>
			</div>

			<!-- Player -->
			<div class="Bigaudio-player">
				<EwLoadingOverlay v-if=" loading " />
				<BigSoundPlayer
					v-if="validPlaylistMode.length"
					:sounds="validPlaylistMode"
					:darkMode="false"
					:draggable="false"
				/>
				<div v-else
					class="relative inset-0 flex items-center justify-center h-[100%] z-1">
					<div
						class="text-xl bg-white rounded-2 p-4 font-semibold !text-danger-800 flex items-center">
						<i class="tabler:alert-triangle mr-2 w-5 h-5"></i> {{ labels.noResults }}</div>
				</div>
			</div>

		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';

import { useAppConfig } from '@/composables/useAppConfig';
import type { Callout, ListFilters, PostListResponse, configCollectionMode } from '@/types';
import { messages } from '@/assets/language';
import { useAppStore } from '@/stores/app-store';
import EwLoadingOverlay from '@/components/EwLoadingOverlay.vue';

const {
	currentLng,
	currentMedicalFilter,
	currentStudyType,
	currentstudyMode,
	currentoutcomeType,
	currenttargetAudience,
	currentPublishingFilter,
	currentAuthoringFilter,
	currentaplicationDomain,
	currentDataFilter,
	currentLatestDays,
	currentCollectionMode,
	currentTestyourself,
	currentPAGE_REQ_ID
} = useAppStore();

const AppConfig = useAppConfig();
const route = useRoute();
const router = useRouter();

const labels = computed(() => {
	const key = currentLng.value as keyof typeof messages;
	return messages[key] ?? {};
});
const loading = ref( false );

// ⏪ Butoni për t'u kthyer
const handleBackButtonClick = () => {
	history.back(); // ose: router.back()
};

const defaultFilters = (): ListFilters => ({
	search: null,
	author: [],
	outcomeType: [],
	targetAudience: [],
	inputMaterialType: []
});

const listFilters = ref<ListFilters>(defaultFilters());

const props = defineProps<{
	selectedLanguage?: string;
}>();

// 🎧 Playlist dhe Collection Mode
const playlistMode = ref<Callout[]>([]);
// const validPlaylistMode = computed(() =>
// 	Array.isArray(playlistMode.value) ? playlistMode.value : []
// );
const validPlaylistMode = computed(() => {
	return Array.isArray(playlistMode.value)
		? playlistMode.value.filter(
				(item) => !!item.file_url && item.publishingStatusCode === 'p'
		  )
		: [];
});

watch(validPlaylistMode, (newVal) => {
	console.log('🎧 validPlaylistMode updated:', newVal);
});

const collectionModeData = ref<configCollectionMode | null>(null);

// 🔄 Ndjek ndryshimet nga prindi për gjuhën
watch(
	() => props.selectedLanguage,
	(newLang) => {
		if (newLang) {
			console.log(`Gjuha e re nga prindi: ${newLang}`);
		}
	}
);

// 🚀 Fetchimi i të dhënave
onMounted(async () => {
	loading.value = true;
	try {
		const params: Record<string, any> = {
			...route.query,
			...listFilters.value,
			'collection-mode': currentCollectionMode.value,
			testYourself: currentTestyourself.value,
			PAGE_REQ_ID: currentPAGE_REQ_ID.value,
			...(currentMedicalFilter.value && { medicalSpeciality: currentMedicalFilter.value }),
			...(currentPublishingFilter.value && { publishingStatus: currentPublishingFilter.value }),
			...(currentAuthoringFilter.value && { authoringStatus: currentAuthoringFilter.value }),
			...(currentStudyType.value && { currentStudyType: currentStudyType.value }),
			...(currentstudyMode.value && { 'flag-mode': currentstudyMode.value }),
			...(currentoutcomeType.value && { outcomeType: currentoutcomeType.value }),
			...(currenttargetAudience.value && { targetAudience: [currenttargetAudience.value] }),
			...(currentaplicationDomain.value && { aplicationDomain: [currentaplicationDomain.value] }),
			...(currentDataFilter.value && { 'dashboard-filter': currentDataFilter.value }),
			...(currentLatestDays.value && { 'latest-days': currentLatestDays.value }),
			...(listFilters.value.allStatuses?.length && { statuses: listFilters.value.allStatuses })
		};

		const response = await axios.get<PostListResponse>(`${AppConfig.urls.getList}?json=y`, {
			params,
			headers: {
				'Content-Type': 'application/json',
			    'X-Requested-With': 'XMLHttpRequest',
				'Accept-Language': currentLng.value
			}
		});

		const jsonData = response.data;
		if (Array.isArray(jsonData.data)) {
			playlistMode.value = jsonData.data;
		} else {
			console.error('⚠️ Expected array, got:', jsonData.data);
			playlistMode.value = [];
		}
		collectionModeData.value = response.data.config;
	} catch (error) {
		console.error('❌ Failed to fetch data:', error);
	}
	 finally {
		loading.value = false;
	}
});
</script>
<style>
.Bigaudio-player {
	height: 100%;
	display: flex;
	flex-direction: column;
	position: static;
	top: 0;
}

.Bigaudio-player .soundPlayer {
	transform: translate(0%, 0%);
	width: auto;
	position: unset !important;
	background: transparent;
	height: 100%;
	display: flex;
	flex-direction: column;
	padding: 15px 0;
	box-shadow: none;
}

.Bigaudio-player .soundPlayer-content {
	display: flex;
	align-items: center;
	position: unset;
	box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);
	padding: 15px;
	border-radius: 8px;
	margin-bottom: 20px;
	border: 1px solid #ededed;
	background: #fff;
}

.Bigaudio-player .soundPlayer-soundList {
	background: rgb(255 255 255);
	color: #fff;
	width: 100%;
	height: 100%;
	max-height: 750px !important;
	display: flex;
	flex-direction: column;
	padding: 15px;
	position: unset !important;
	transform: translate(0, 0);
}

@media only screen and (max-width: 767px) {
	.Bigaudio-player .soundPlayer-soundList-content {
		overflow-x: hidden;
	}
}

@media only screen and (min-width: 768px) {
	.Bigaudio-player .soundPlayer-soundList-content {
		max-height: 100% !important;
	}
}

.Bigaudio-player .soundPlayer-slider {
	width: 100% !important;
}

.Bigaudio-player .soundPlayer-soundList button {
	margin: 0 !important;
	padding-left: 0;
	padding-right: 0;
}

.Bigaudio-player .soundPlayer-soundList-item:nth-child(n + 2) {
	border-color: rgba(209, 213, 219);
}

@media only screen and (max-width: 767px) {
	.Bigaudio-player .soundPlayer-soundList-content {
		max-height: 100% !important;
		overflow-y: unset !important;
	}
}
</style>
